import { useQuery } from "@tanstack/react-query";
import { api } from "../api.js";
import { useWorkspace } from "./workspace.js";

/**
 * No credit, nothing runs (ADR 0048, made a screen on 2026-09-24).
 *
 * Every paid call is refused by `runCall` when there is no credit, and that was always true. What was not true is that
 * the screens said so: a workspace at $0 typed its website, every step of the lookup was refused, and the page showed
 * "could not find you on Google" as if it had looked. So the shell says it before anything is asked:
 *
 * - **Never bought anything**: the account dialog, held on Credits, over an empty shell — the packs, and no way out but
 *   buying or signing out. Nothing in the product can do anything for them yet, and a tab that let them try would only
 *   fail more politely. It was a page of its own until the owner asked for the dialog instead (2026-09-24).
 * - **Bought before, and spent it**: a strip across the top, and the tabs still open, because what they made is theirs
 *   to look at, trim, download and post by hand. What waits is anything new.
 *
 * The two are told apart by whether the account holds any lot at all, not by the balance (ADR 0048 point 5). Credits
 * is asked for only once the balance reads zero, so a workspace with money pays nothing for this.
 */
export function useNoCredit(): "first" | "spent" | null {
  const ws = useWorkspace();
  const empty = ws.data ? Number(ws.data.balanceUsd) <= 0 : false;
  const credits = useQuery({ queryKey: ["credits"], queryFn: api.credits, enabled: empty });
  if (!empty || !credits.data) return null;
  return credits.data.lots.total === 0 ? "first" : "spent";
}

/** Across the top of every tab once the credit bought has been spent. */
export function CreditSpent({ onCredits }: { onCredits: () => void }) {
  return (
    <div role="status" className="shrink-0 flex flex-wrap items-center gap-x-3 gap-y-1 px-4 py-2 bg-tint border-b border-coral/30 text-[12.5px]">
      <span className="font-semibold">No credit left.</span>
      <span className="text-ink-2 flex-1 min-w-[200px]">Nothing new can run until you add some. Everything you have made is still here.</span>
      <button onClick={onCredits} className="px-3 py-1 rounded-lg bg-ink text-white text-xs font-semibold">Add credit</button>
    </div>
  );
}
